/**
 * HTTP tenant service — gateway `/tenants` endpoints (proxied to
 * taxhive-security-service tenant_routes).
 *
 * Contract notes (verified against taxhive-security-service tenant_routes.py):
 *  - Get one:      `GET   /tenants/{tenant_id}`        → TenantResponse.
 *  - Update:       `PATCH /tenants/{tenant_id}`        with TenantUpdateRequest
 *                  (`tenant_name | firm_gstin | firm_pan | firm_email |
 *                   firm_phone | firm_address | settings`).
 *  - Subscription: `GET   /tenants/{tenant_id}/subscription` → SubscriptionResponse
 *                  (404 when none — surfaced here as `null`).
 *
 * NOTE: the gateway proxy for `/tenants` is added by settings_routes.py (this
 * task). The TenantResponse is admin-trimmed (no settings/address round-trip is
 * guaranteed yet), so verify field availability during real-mode testing.
 */

import axios from 'axios'
import { apiClient, toServiceError } from '@/services/http/client'
import { toSnakeDeep } from '@/services/http/caseMap'
import type { TenantService } from './tenant.service'
import type { BillingCycle, BillingOverview, EffectiveEntitlements, PlanCommandInput, SubscriptionCommandResult, Tenant, Subscription, UpdateTenantInput } from '@/types/tenant.types'
import { mapBillingOverviewFromApi, mapEffectiveEntitlementsFromApi, mapSubscriptionCommandFromApi, mapTenantFromApi, mapSubscriptionFromApi, mapUpdateToApi } from './mappers'

export function createHttpTenantService(): TenantService {
  return {
    async get(tenantId: string): Promise<Tenant> {
      try {
        const res = await apiClient.get(`/tenants/${tenantId}`)
        return mapTenantFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async update(tenantId: string, patch: UpdateTenantInput): Promise<Tenant> {
      try {
        const res = await apiClient.patch(`/tenants/${tenantId}`, mapUpdateToApi(patch))
        return mapTenantFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getSubscription(tenantId: string): Promise<Subscription | null> {
      try {
        const res = await apiClient.get(`/tenants/${tenantId}/subscription`)
        return mapSubscriptionFromApi(res.data)
      } catch (err) {
        // No active subscription is a 404 — treat as "none" rather than an error.
        if (axios.isAxiosError(err) && err.response?.status === 404) return null
        throw toServiceError(err)
      }
    },

    async getEffectiveEntitlements(tenantId: string): Promise<EffectiveEntitlements> {
      try {
        const res = await apiClient.get(`/tenants/${tenantId}/entitlements`)
        return mapEffectiveEntitlementsFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getBillingOverview(tenantId: string): Promise<BillingOverview> {
      try {
        const res = await apiClient.get(`/tenants/${tenantId}/billing/overview`)
        return mapBillingOverviewFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async purchaseSubscription(tenantId: string, input: PlanCommandInput): Promise<SubscriptionCommandResult> {
      try {
        const res = await apiClient.post(`/tenants/${tenantId}/billing/purchase`, toSnakeDeep(input))
        return mapSubscriptionCommandFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async changeSubscriptionPlan(tenantId: string, input: PlanCommandInput): Promise<SubscriptionCommandResult> {
      try {
        const res = await apiClient.post(`/tenants/${tenantId}/billing/plan-change`, toSnakeDeep(input))
        return mapSubscriptionCommandFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async renewSubscription(tenantId: string, input: { billingCycle?: BillingCycle; idempotencyKey: string }): Promise<SubscriptionCommandResult> {
      try {
        const res = await apiClient.post(`/tenants/${tenantId}/billing/renew`, toSnakeDeep(input))
        return mapSubscriptionCommandFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async cancelSubscription(tenantId: string, input: { reason: string; idempotencyKey: string }): Promise<SubscriptionCommandResult> {
      try {
        const res = await apiClient.post(`/tenants/${tenantId}/billing/cancel`, toSnakeDeep(input))
        return mapSubscriptionCommandFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async reactivateSubscription(tenantId: string, input: { billingCycle?: BillingCycle; idempotencyKey: string }): Promise<SubscriptionCommandResult> {
      try {
        const res = await apiClient.post(`/tenants/${tenantId}/billing/reactivate`, toSnakeDeep(input))
        return mapSubscriptionCommandFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
